import { useState, type FormEvent } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  EMPLOYEE_PERMISSIONS,
  normalizePermissions,
  normalizeUsername,
  validateDisplayName,
  validatePin,
  validateUsername,
  type PermissionSet,
} from '@pos/core';
import { patchRow, type MembershipRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { callFunction } from '../../lib/supabase.ts';
import { useOnline } from '../../lib/use-online.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Badge, Button, Card, Field, Input, PageHeader, Toggle, errorMessage } from '../../ui/index.tsx';

export function EmployeesPage() {
  const { business } = useSession();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const members = useLiveQuery(
    () => db.memberships.where('business_id').equals(business.id).toArray(),
    [business.id],
  );
  // Los eliminados no se listan (siguen existiendo para el historial: ventas, cajas, reportes).
  const sorted = [...(members ?? [])].filter((m) => !m.deleted_at).sort(
    (a, b) =>
      Number(b.role === 'owner') - Number(a.role === 'owner') ||
      Number(b.active) - Number(a.active) ||
      a.display_name.localeCompare(b.display_name, 'es'),
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Empleados"
        description="Cada empleado entra con su usuario y PIN, solo en dispositivos habilitados."
        actions={editing !== 'new' && <Button onClick={() => setEditing('new')}>Nuevo empleado</Button>}
      />

      {editing === 'new' && <NewEmployeeForm onDone={() => setEditing(null)} />}

      <Card flush className="divide-y divide-slate-100">
        {sorted.map((m) =>
          editing === m.id ? (
            <div key={m.id} className="p-4 sm:p-6">
              <EditEmployeeForm membership={m} onDone={() => setEditing(null)} />
            </div>
          ) : (
            <div key={m.id} className="flex items-center gap-3 p-4">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-slate-100 font-semibold text-slate-700">
                {m.display_name.charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{m.display_name}</p>
                <p className="truncate text-sm text-slate-500">{m.role === 'owner' ? 'Dueño' : `Usuario: ${m.username}`}</p>
              </div>
              {!m.active && <Badge tone="red">Inactivo</Badge>}
              {m.role === 'employee' && (
                <Button variant="secondary" onClick={() => setEditing(m.id)}>
                  Editar
                </Button>
              )}
            </div>
          ),
        )}
      </Card>
    </div>
  );
}

function PermissionsEditor({ value, onChange }: { value: PermissionSet; onChange: (v: PermissionSet) => void }) {
  return (
    <div className="divide-y divide-slate-100">
      {EMPLOYEE_PERMISSIONS.map((p) => (
        <Toggle
          key={p.key}
          label={p.label}
          description={p.description}
          checked={value[p.key] === true}
          onChange={(checked) => onChange({ ...value, [p.key]: checked })}
        />
      ))}
      {value.apply_discounts && (
        <div className="py-3">
          <Field label="Descuento máximo por venta (%)">
            {(id) => (
              <Input
                id={id}
                type="number"
                inputMode="numeric"
                min={0}
                max={100}
                className="max-w-32"
                value={value.max_discount_pct ?? 10}
                onChange={(e) => onChange({ ...value, max_discount_pct: Number(e.target.value) })}
              />
            )}
          </Field>
        </div>
      )}
    </div>
  );
}

function NewEmployeeForm({ onDone }: { onDone: () => void }) {
  const { business } = useSession();
  const online = useOnline();
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [permissions, setPermissions] = useState<PermissionSet>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    const add = (key: string, message: string | null) => message && (next[key] = message);
    add('displayName', validateDisplayName(displayName));
    add('username', validateUsername(username));
    add('pin', validatePin(pin));
    setErrors(next);
    if (Object.keys(next).length) return;

    setBusy(true);
    setError(null);
    try {
      const { membership } = await callFunction<{ membership: MembershipRow }>('create-employee', {
        business_id: business.id,
        display_name: displayName.trim(),
        username: normalizeUsername(username),
        pin,
        permissions: normalizePermissions(permissions),
      });
      await db.memberships.put(membership);
      requestSync(0);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <form className="space-y-4" onSubmit={onSubmit}>
        <h2 className="font-semibold">Nuevo empleado</h2>
        {!online && <Alert tone="warning">Para dar de alta un empleado hace falta conexión a internet.</Alert>}
        {error && <Alert tone="error">{error}</Alert>}
        <Field label="Nombre" error={errors.displayName}>
          {(id) => <Input id={id} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Usuario" hint="Con esto entra al sistema. Ej: juan" error={errors.username}>
            {(id) => (
              <Input
                id={id}
                autoCapitalize="none"
                autoCorrect="off"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            )}
          </Field>
          <Field label="PIN" hint="4 a 6 números. Anotáselo al empleado." error={errors.pin}>
            {(id) => (
              <Input
                id={id}
                inputMode="numeric"
                autoComplete="off"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              />
            )}
          </Field>
        </div>
        <div>
          <p className="text-sm font-medium text-slate-700">Permisos</p>
          <p className="text-sm text-slate-500">Siempre puede vender y consultar stock. Nunca ve costos ni ganancias.</p>
          <PermissionsEditor value={permissions} onChange={setPermissions} />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onDone}>
            Cancelar
          </Button>
          <Button type="submit" loading={busy} disabled={!online}>
            Crear empleado
          </Button>
        </div>
      </form>
    </Card>
  );
}

function EditEmployeeForm({ membership, onDone }: { membership: MembershipRow; onDone: () => void }) {
  const { writer } = useSession();
  const online = useOnline();
  const [displayName, setDisplayName] = useState(membership.display_name);
  const [active, setActive] = useState(membership.active);
  const [permissions, setPermissions] = useState<PermissionSet>(membership.permissions);
  const [newPin, setNewPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  // El resultado del cambio de PIN se muestra junto al campo: arriba del formulario queda fuera de la vista.
  const [pinMessage, setPinMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const save = async () => {
    const nameError = validateDisplayName(displayName);
    if (nameError) return setMessage({ tone: 'error', text: nameError });
    const changes: Record<string, unknown> = {};
    if (displayName.trim() !== membership.display_name) changes.display_name = displayName.trim();
    if (active !== membership.active) changes.active = active;
    const normalized = normalizePermissions(permissions);
    if (JSON.stringify(normalized) !== JSON.stringify(normalizePermissions(membership.permissions))) {
      changes.permissions = normalized;
    }
    if (Object.keys(changes).length === 0) return onDone();
    setBusy(true);
    try {
      await patchRow(db, 'memberships', membership.id, changes, writer);
      requestSync(0);
      onDone();
    } catch (err) {
      setMessage({ tone: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (
      !window.confirm(
        `¿Eliminar a ${membership.display_name}? Sale de la lista y su usuario «${membership.username}» queda libre para otra persona. Sus ventas y movimientos siguen registrados a su nombre. No se puede deshacer.`,
      )
    )
      return;
    setBusy(true);
    try {
      await patchRow(db, 'memberships', membership.id, { deleted_at: new Date().toISOString() }, writer);
      requestSync(0);
      onDone();
    } catch (err) {
      setMessage({ tone: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const changePin = async () => {
    const pinError = validatePin(newPin);
    if (pinError) return setPinMessage({ tone: 'error', text: pinError });
    setBusy(true);
    setPinMessage(null);
    try {
      await callFunction('set-employee-pin', { membership_id: membership.id, pin: newPin });
      setNewPin('');
      setPinMessage({ tone: 'success', text: 'PIN actualizado. Si estaba bloqueado por intentos, ya puede entrar.' });
    } catch (err) {
      setPinMessage({ tone: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="font-semibold">Editar a {membership.display_name}</h2>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      <Field label="Nombre">
        {(id) => <Input id={id} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />}
      </Field>
      <Toggle
        checked={active}
        onChange={setActive}
        label="Activo"
        description="Si lo desactivás, no puede entrar en ningún dispositivo."
      />
      <div>
        <p className="text-sm font-medium text-slate-700">Permisos</p>
        <PermissionsEditor value={permissions} onChange={setPermissions} />
      </div>
      <div className="rounded-lg bg-slate-50 p-3">
        <Field label="Cambiar PIN" hint={online ? undefined : 'Requiere conexión.'}>
          {(id) => (
            <div className="flex gap-2">
              <Input
                id={id}
                inputMode="numeric"
                autoComplete="off"
                maxLength={6}
                placeholder="Nuevo PIN"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
              />
              <Button variant="secondary" onClick={changePin} loading={busy} disabled={!online || newPin.length < 4}>
                Cambiar
              </Button>
            </div>
          )}
        </Field>
        {pinMessage && (
          <div className="mt-3">
            <Alert tone={pinMessage.tone}>{pinMessage.text}</Alert>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {!membership.active ? (
          <Button variant="dangerGhost" onClick={remove} disabled={busy}>
            Eliminar empleado
          </Button>
        ) : (
          <span className="text-xs text-slate-500">Para eliminarlo, primero desactivalo y guardá.</span>
        )}
        <span className="flex gap-2">
          <Button variant="secondary" onClick={onDone}>
            Cancelar
          </Button>
          <Button onClick={save} loading={busy}>
            Guardar
          </Button>
        </span>
      </div>
    </div>
  );
}
